import { useTranslation } from 'next-i18next';

import { Box, Flex, HStack, StackProps, Text } from '@chakra-ui/react';

import { ListingAvailableOptions } from '@nocowanie/noc-api';

import { IconsLinear } from '@nocowanie/icons/linear';

import { filterComponents } from '@app/components/organisms/filter-components';
import { FiltersDrawer } from '@app/components/organisms/filters-drawer';
import { useInitialRenderFlag } from '@app/hooks/useInitialRenderFlag';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { useSearchFilters } from '@app/hooks/useSearchFilters';

export const MapFilters = ({
    isMapLoaded = true,
    availableOptions,
}: {
    isMapLoaded?: boolean;
    availableOptions: ListingAvailableOptions | null;
}) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'filters',
    });

    const { isFiltersContentVisible, realtimeBookingModelAccommodationsCount, isMobileRes } =
        useMapSearch();
    const { handleApplyFilters, handleClearFilters, filterItems, createReservationSwitch } =
        useSearchFilters({ availableOptions });
    const isInitialLoad = useInitialRenderFlag();

    const filtersContent = (
        <Flex direction="column" gap={8}>
            {createReservationSwitch({
                accommodationCount: realtimeBookingModelAccommodationsCount,
            })}

            {filterItems?.map((item, index) =>
                filterComponents[item.componentType]?.(
                    {
                        ...item,
                        isLoading: isInitialLoad,
                    },
                    index,
                ),
            )}
        </Flex>
    );

    const OTAInfoBox = ({ ...props }: StackProps) => (
        <HStack bg={'secondary.50'} borderRadius={'md'} px={3} py={4} {...props}>
            <IconsLinear.InfoCircle size="md" color={'secondary.solid'} transform={'scaleY(-1)'} />
            <Box fontSize={'xs'}>
                <Box fontWeight={'bold'}>{t('onlineBookingLabel')}</Box>
                <Box color={'secondary.solid'}>{t('onlineBookingDesc')}</Box>
            </Box>
        </HStack>
    );

    if (isMobileRes) {
        return (
            <FiltersDrawer onApplyFilters={handleApplyFilters} onClearFilters={handleClearFilters}>
                <OTAInfoBox mb={4} />

                {filtersContent}
            </FiltersDrawer>
        );
    }

    return (
        <Box
            h="100vh"
            w={isFiltersContentVisible && isMapLoaded ? 'mapSidebarWidth' : '0px'}
            transition={isInitialLoad ? 'none' : 'width 0.3s ease-in-out'}
            boxShadow={'2xl'}
            overflow={'hidden'}
        >
            <Flex
                direction="column"
                h="100%"
                w={'mapSidebarWidth'}
                overflowY="auto"
                overflowX="hidden"
                opacity={isFiltersContentVisible ? 1 : 0}
                transition={isInitialLoad ? 'none' : 'opacity 0.2s 0.1s'}
                pl={5}
                pr={3}
                py={4}
                gap={5}
            >
                <Flex justifyContent={'space-between'} alignItems={'center'}>
                    <Text fontSize="2xl" fontWeight={'bold'}>
                        {t('filter')}
                    </Text>
                    <Text
                        as="button"
                        onClick={() => {
                            handleClearFilters(true);
                        }}
                        color={'secondary.600'}
                        cursor={'pointer'}
                        textDecoration={'underline'}
                        _hover={{
                            textDecoration: 'none',
                        }}
                    >
                        {t('clearAll')}
                    </Text>
                </Flex>
                <OTAInfoBox />
                {filtersContent}
            </Flex>
        </Box>
    );
};
